export function resolveOrgFromHostname(hostname: string = window.location.hostname): string {
  const labels = hostname.split('.');
  // A leading `www.` is a CDN/vanity prefix, not the org slug.
  if (labels[0] === 'www' && labels.length > 1) return labels[1];
  return labels[0];
}

/** Ondemand orgs are provisioned with a 6-char hex suffix: scusd-e92cfb */
export const ONDEMAND_ORG_SLUG_PATTERN = /^[a-z0-9][\w-]*-[a-z0-9]{6}$/i;

export function isOndemandOrg(hostname: string = window.location.hostname): boolean {
  const org = resolveOrgFromHostname(hostname);
  return ONDEMAND_ORG_SLUG_PATTERN.test(org);
}

/** Base domain: `demand.schoolmint.com` for ondemand, else `schoolmint.com`. */
export function resolveSchoolmintDomain(hostname: string = window.location.hostname): string {
  return isOndemandOrg(hostname) ? 'demand.schoolmint.com' : 'schoolmint.com';
}

/**
 * "Apply Now" target — the org's enrollment login on schoolmint.com, e.g.
 * `https://scusd.schoolmint.com/login`. Ondemand orgs use demand.schoolmint.com.
 * Null when the org slug can't be resolved from the hostname.
 */
export function resolveApplyNowUrl(hostname: string = window.location.hostname): string | null {
  const org = resolveOrgFromHostname(hostname);
  if (!org) return null;
  return `https://${org}.${resolveSchoolmintDomain(hostname)}/login`;
}

/** Local dev finder hosts: `*.finder.localhost` / `*.finder.localhost.test`. */
export function isLocalhostHost(hostname: string = window.location.hostname): boolean {
  return hostname.endsWith('.localhost.test');
}

/**
 * Admin (enrollment-fe) origin for the preview login bounce.
 * - local `{org}.finder.localhost.test` → `http://{org}.localhost.test:3000`
 * - ondemand → fixed `https://{org}.demand.schoolmint.com` (finder env label
 *   doesn't map to the admin host, e.g. dev finder → demand admin)
 * - regular → drop the `finder` label, preserving any env label:
 *   `{org}.finder.schoolmint.com` → `https://{org}.schoolmint.com`;
 *   `{org}.dev.finder.schoolmint.com` → `https://{org}.dev.schoolmint.com`
 */
export function resolveAdminOrigin(hostname: string = window.location.hostname): string {
  if (isLocalhostHost(hostname)) {
    return `http://${hostname.replace('.finder.', '.')}:3000`;
  }
  if (isOndemandOrg(hostname)) {
    return `https://${resolveOrgFromHostname(hostname)}.demand.schoolmint.com`;
  }
  return `https://${hostname.replace('.finder.', '.')}`;
}

/** Premium admin login URL for the preview bounce, with `redirect_to` back to the finder. */
export function resolvePreviewLoginUrl(hostname: string = window.location.hostname): string | null {
  const org = resolveOrgFromHostname(hostname);
  if (!org) return null;
  // Mark the return URL so we never bounce twice (loop guard).
  const target = new URL(window.location.href);
  target.searchParams.set('preview_auth', '1');
  const redirectTo = encodeURIComponent(target.href);
  return `${resolveAdminOrigin(hostname)}/manage/login?redirect_to=${redirectTo}`;
}
